import { Button } from "@/components/ui/button";
import { buildMetadata } from "@/lib/metadata";
import { Link, createFileRoute } from "@tanstack/react-router";
import {
  AlertCircle,
  ArrowLeft,
  ArrowUp,
  File,
  FileImage,
  FileSpreadsheet,
  FileText,
  type LucideIcon,
} from "lucide-react";
import { z } from "zod";

const DEFAULT_FAILURE_REASON =
  "The uploaded file couldn't be processed due to critical issues. No orders were created.";

const formatFileSize = (size: number) => {
  if (size <= 0) {
    return "0 KB";
  }

  const units = ["B", "KB", "MB", "GB"];
  const exponent = Math.min(
    Math.floor(Math.log(size) / Math.log(1024)),
    units.length - 1,
  );
  const formatted = size / 1024 ** exponent;

  return `${formatted.toFixed(exponent === 0 ? 0 : 1)} ${units[exponent]}`;
};

const formatUploadedAt = (uploadedAt: string) => {
  const parsedDate = new Date(uploadedAt);

  if (Number.isNaN(parsedDate.getTime())) {
    return "Uploaded just now";
  }

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(parsedDate);
};

type FileIconSpec = {
  icon: LucideIcon;
  iconClassName: string;
  wrapperClassName: string;
};

const DEFAULT_FILE_ICON: FileIconSpec = {
  icon: File,
  iconClassName: "text-slate-600",
  wrapperClassName: "bg-slate-100",
};

const SPREADSHEET_FILE_ICON: FileIconSpec = {
  icon: FileSpreadsheet,
  iconClassName: "text-emerald-700",
  wrapperClassName: "bg-emerald-50",
};

const TEXT_FILE_ICON: FileIconSpec = {
  icon: FileText,
  iconClassName: "text-sky-700",
  wrapperClassName: "bg-sky-50",
};

const IMAGE_FILE_ICON: FileIconSpec = {
  icon: FileImage,
  iconClassName: "text-amber-700",
  wrapperClassName: "bg-amber-50",
};

const getFileExtension = (fileName: string) => {
  const parts = fileName.toLowerCase().split(".");
  return parts.length > 1 ? parts[parts.length - 1] : "";
};

const getFileIcon = (fileName: string, mimeType: string): FileIconSpec => {
  const normalizedMimeType = mimeType.toLowerCase();
  const extension = getFileExtension(fileName);

  if (
    normalizedMimeType.startsWith("image/") ||
    ["gif", "heic", "jpeg", "jpg", "png", "svg", "webp"].includes(extension)
  ) {
    return IMAGE_FILE_ICON;
  }

  if (
    [
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "text/csv",
    ].includes(normalizedMimeType) ||
    ["csv", "xls", "xlsx"].includes(extension)
  ) {
    return SPREADSHEET_FILE_ICON;
  }

  if (
    [
      "application/msword",
      "application/pdf",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "text/plain",
    ].includes(normalizedMimeType) ||
    ["doc", "docx", "pdf", "txt"].includes(extension)
  ) {
    return TEXT_FILE_ICON;
  }

  return DEFAULT_FILE_ICON;
};

export const Route = createFileRoute("/_main/orders/upload-failed")({
  component: RouteComponent,
  validateSearch: z.object({
    fileName: z.string().catch("Uploaded file"),
    fileSize: z.string().catch("0"),
    mimeType: z.string().catch(""),
    uploadedAt: z.string().catch(""),
    reasons: z.string().catch(""),
  }),
  head: () => ({
    meta: buildMetadata({
      title: "Upload Failed • Openfarming",
      description: "Review why your order upload could not be processed.",
      keywords: ["orders", "upload", "failed"],
    }),
  }),
});

function RouteComponent() {
  const { fileName, fileSize, mimeType, uploadedAt, reasons } =
    Route.useSearch();
  const rawFileSize = Number.parseFloat(fileSize);
  const parsedFileSize = Number.isFinite(rawFileSize) ? rawFileSize : 0;
  const fileIcon = getFileIcon(fileName, mimeType);
  const FileIconComponent = fileIcon.icon;
  const parsedReasons = (() => {
    try {
      const value: unknown = JSON.parse(reasons);

      if (!Array.isArray(value)) {
        return [];
      }

      return value.filter(
        (reason): reason is string =>
          typeof reason === "string" && reason !== "",
      );
    } catch {
      return [];
    }
  })();
  const visibleReasons =
    parsedReasons.length > 0 ? parsedReasons : [DEFAULT_FAILURE_REASON];

  return (
    <div className="flex w-full flex-col gap-8 px-6 py-8 md:px-8 md:py-10">
      <div className="flex w-full flex-col gap-6">
        <Link
          to="/orders/upload"
          className="inline-flex items-center gap-2 text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="size-4" />
          Back to Upload
        </Link>

        <section className="flex items-start gap-4 pt-1">
          <AlertCircle
            className="mt-0.5 size-9 shrink-0 text-[#ff5d5f] md:size-10"
            strokeWidth={2.25}
          />
          <div className="space-y-2 md:space-y-3">
            <h1 className="font-medium text-3xl text-[#ff5d5f] tracking-[-0.03em] md:text-4xl">
              Upload Failed
            </h1>
            <p className="max-w-4xl text-[#151515] text-base leading-relaxed tracking-[-0.02em] md:text-lg">
              The uploaded file couldn&apos;t be processed due to critical
              issues. No orders we created
            </p>
          </div>
        </section>

        <section className="rounded-[24px] bg-[#f7f8fb] px-5 py-5 md:px-6 md:py-6">
          <div className="flex items-center gap-4">
            <span
              className={`flex size-11 shrink-0 items-center justify-center rounded-2xl ${fileIcon.wrapperClassName} md:size-12`}
            >
              <FileIconComponent
                className={`size-5 ${fileIcon.iconClassName} md:size-6`}
              />
            </span>
            <div className="min-w-0 space-y-1">
              <p className="truncate font-semibold text-[#111111] text-base tracking-[-0.02em] md:text-lg">
                {fileName}
              </p>
              <p className="text-[#7d8793] text-sm tracking-[-0.02em] md:text-base">
                {formatFileSize(parsedFileSize)}
                {uploadedAt !== ""
                  ? ` • Uploaded ${formatUploadedAt(uploadedAt)}`
                  : ""}
              </p>
            </div>
          </div>
        </section>

        <section className="rounded-[24px] border border-[#ff6a6d] bg-[#fff7f7] px-5 py-6 md:px-6 md:py-7">
          <h2 className="font-semibold text-[#ff6668] text-lg tracking-[-0.02em] md:text-xl">
            Reasons detected
          </h2>
          <ul className="mt-5 space-y-2 pl-6 text-[#484f59] text-sm leading-relaxed tracking-[-0.02em] marker:text-[#3d434b] md:mt-6 md:text-base">
            {visibleReasons.map((reason) => (
              <li key={reason} className="list-disc">
                {reason}
              </li>
            ))}
          </ul>
        </section>

        <div className="flex justify-end pt-1">
          <Button
            asChild
            className="h-11 rounded-xl bg-black px-5 font-medium text-sm text-white tracking-[-0.02em] hover:bg-black/95 md:h-12 md:px-6 md:text-base"
          >
            <Link to="/orders/upload" className="gap-3">
              Reupload File
              <ArrowUp className="size-4" strokeWidth={2.3} />
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
